import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { objectOmit } from '@/_shared/helpers';
import { GenericPost } from '@/_shared/types';
import { GenericApiError } from '@/_shared/types/apiError';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_EMPLOYEES_IMPORTS_FILE_PATH_KEY } from '@/swagger/constants/anagraphics-keys';
import {
  API_POST_EMPLOYEES_IMPORTS_TEMPLATE,
  KEY_POST_EMPLOYEES_IMPORTS_TEMPLATE,
} from '../constants';
import { MOCK_RESPONSE_POST_EMPLOYEES_IMPORTS_TEMPLATE } from '../mocks';

type RequestEmployeesImportsTemplate = GenericPost<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_EMPLOYEES_IMPORTS_FILE_PATH_KEY
>['request']['body'];

export type CallParamsPostEmployeesImportsTemplate = RequestEmployeesImportsTemplate & {
  file: File;
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponsePostEmployeesImportsTemplate = GenericPost<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_EMPLOYEES_IMPORTS_FILE_PATH_KEY
>['response']['body'];

type CallErrorPostEmployeesImportsTemplate = GenericApiError;

type CallSelectorsPostEmployeesImportsTemplate = Partial<
  GenericPost<
    typeof API_ORGANIZATIONS_ORGANIZATIONID_EMPLOYEES_IMPORTS_FILE_PATH_KEY
  >['response']['body']
>;

export const postEmployeesImportsTemplateCall = createGeneralCall<
  CallParamsPostEmployeesImportsTemplate,
  CallResponsePostEmployeesImportsTemplate,
  CallErrorPostEmployeesImportsTemplate,
  CallSelectorsPostEmployeesImportsTemplate
>({
  id: KEY_POST_EMPLOYEES_IMPORTS_TEMPLATE,
  url: () => state => {
    const organizationId = getCurrentOrganization(state);
    return API_POST_EMPLOYEES_IMPORTS_TEMPLATE(organizationId);
  },
  method: 'post',
  paramsSelector:
    ({ payload }) =>
    () => {
      const formData = new FormData();
      const payloadWithoutSAgas = objectOmit(payload, ['successSaga', 'errorSaga']);
      Object.entries(payloadWithoutSAgas).forEach(([key, value]) => {
        formData.append(key, value);
      });

      return formData;
    },
  options: {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    bodyHasOriginal: true,
  },
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_POST_EMPLOYEES_IMPORTS_TEMPLATE : undefined,
  selectors: {
    jobId: ({ selectors }) => createSelector(selectors.response, res => res?.jobId || undefined),
  },
});
